'use client'

import { ACCOUNT_LENS_ADDRESS, E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { useUserCollateralState } from '@/domain/euler/rebalance'
import type { BorrowPosition, CollateralPosition, LtvConfig } from '@/domain/euler/rebalance'
import { useCleanupClosedPositionNames } from '@/domain/euler/rebalance/useCleanupClosedPositionNames'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { useCardSolverConfigs, useStoreHydrated } from '@/domain/state'
import type { PersistedCardSolverConfig } from '@/domain/state/cardSolverConfigs'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CardRouteBadge } from '@/features/card/components/CardRouteBadge'
import type { CardRouteBadgeData } from '@/features/card/utils/cardSolverRouteMatching'
import {
  getEulerBorrowCardRouteBadge,
  getEulerSpendCardRouteBadge,
} from '@/features/card/utils/cardSolverRouteMatching'
import { eBorrowMoreDialogConfig } from '@/features/eborrow-details/dialogs/borrow-more/EBorrowMoreDialog'
import { eCollateralStatusDialogConfig } from '@/features/eborrow-details/dialogs/collateral-status/ECollateralStatusDialog'
import {
  eBorrowUnifiedRepayDialogConfig,
  type CollateralOption,
  type EBorrowUnifiedRepayDialogProps,
} from '@/features/eborrow-details/dialogs/repay/EBorrowUnifiedRepayDialog'
import { eWithdrawCollateralDialogConfig } from '@/features/eborrow-details/dialogs/withdraw-collateral/EWithdrawCollateralDialog'
import { eAddToPositionDialogConfig } from '@/features/eborrow-details/dialogs/add-to-position/EAddToPositionDialog'
import { evcSwapDebtDialogConfig } from '@/features/eborrow-details/dialogs/swap-debt/EvcSwapDebtDialog'
import { positionAssetSwapDialogConfig } from '@/features/evault-details/dialogs/asset-swap/PositionAssetSwapDialog'
import { eTransferDialogConfig } from '@/features/eborrow-details/dialogs/transfer/ETransferDialog'
import { getEntities } from '@/features/evault-details/data/entities'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { useEulerIndexerELends } from '@/domain/euler/elends/elendsQuery'
import type { NormalizedELendInfo } from '@/domain/euler/elends/types'
import { Button } from '@/ui/atoms/button/Button'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/atoms/dropdown/DropdownMenu'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SupplyApyTooltip } from '@/ui/molecules/supply-apy-tooltip/SupplyApyTooltip'
import { cn } from '@/ui/utils/style'
import * as Collapsible from '@/ui/atoms/collapsible/Collapsible'
import { accountLensAbi } from 'abis/evm/accountLens'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { Check, ChevronDownIcon, HelpCircle, MoreVertical, Pencil, Plus, X } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo, useRef, useState } from 'react'
import { type Address, formatUnits } from 'viem'
import { type UseReadContractsParameters, useAccount, useReadContracts } from 'wagmi'
import { paths } from '@/config/paths'
import { usePositionName } from '@/domain/euler/positions/usePositionName'

export interface SubAccountPositionsProps {
  /** Vault addresses to query positions for */
  vaultAddresses: Address[]
  /** LTV configs for calculating health factor (optional) */
  ltvConfigs?: LtvConfig[]
  /** Map of vault address -> logo URI */
  vaultLogos?: Map<string, string | undefined>
  /** Compact mode for inline display */
  compact?: boolean
  /** Optional className */
  className?: string
  /** Optional title override */
  title?: string
  /** Hide if no positions */
  hideIfEmpty?: boolean
  /**
   * Filter to only show positions matching a specific route.
   * Shows positions where:
   * - Collateral includes filterCollateralVault AND borrow is filterLiabilityVault, OR
   * - Collateral includes filterCollateralVault AND no borrow exists
   */
  filterCollateralVault?: Address
  filterLiabilityVault?: Address
}

export interface SubAccountSummary {
  subAccountId: number
  collaterals: CollateralPosition[]
  borrows: BorrowPosition[]
  controller: Address | null
  /** Total collateral USD for display (from local position data / API prices) */
  totalCollateralUsd: number
  /** Total debt USD for display (from local position data / API prices) */
  totalDebtUsd: number
  /** Health factor from Account Lens */
  healthFactor: number | null
  /** LTV from Account Lens */
  ltv: number | null
  /** Effective max LTV (weighted average across all collaterals) from Account Lens */
  effectiveMaxLtv: number | null
  /** Effective liquidation LTV (weighted average across all collaterals) from Account Lens */
  effectiveLiquidationLtv: number | null
  /** Sub-account address for this position */
  subAccountAddress: Address | null
  /**
   * Collateral value in USD from on-chain oracle (Account Lens collateralValueRaw).
   * Must be used with effectiveMaxLtv for max borrow calculations to ensure
   * consistency with the on-chain EVC liquidity check.
   */
  oracleCollateralUsd: number | null
}

/**
 * Parse Account Lens liquidity info to extract health factor and LTV
 */
function parseAccountLensData(liquidityInfo: {
  queryFailure: boolean
  liabilityValueLiquidation: bigint
  collateralValueLiquidation: bigint
  collateralValueBorrowing: bigint
  collateralValueRaw: bigint
}): {
  healthFactor: number | null
  ltv: number | null
  effectiveMaxLtv: number | null
  effectiveLiquidationLtv: number | null
  totalCollateralUsd: number
  totalDebtUsd: number
} | null {
  // Return null when query fails so downstream ?? fallbacks work correctly
  // (returning 0 would prevent fallback since 0 ?? value evaluates to 0)
  if (liquidityInfo.queryFailure) {
    return null
  }

  const debtUsd = Number(formatUnits(liquidityInfo.liabilityValueLiquidation, 18))
  const collateralUsd = Number(formatUnits(liquidityInfo.collateralValueRaw, 18))
  const collateralLiqUsd = Number(formatUnits(liquidityInfo.collateralValueLiquidation, 18))
  const collateralBorrowUsd = Number(formatUnits(liquidityInfo.collateralValueBorrowing, 18))

  const hasDebt = debtUsd > 0
  const healthFactor = hasDebt ? collateralLiqUsd / debtUsd : null
  const ltv = collateralUsd > 0 ? (debtUsd / collateralUsd) * 100 : null
  // Effective max LTV = collateralValueBorrowing / collateralValueRaw * 100
  // This is the weighted average max LTV across all enabled collaterals
  const effectiveMaxLtv = collateralUsd > 0 ? (collateralBorrowUsd / collateralUsd) * 100 : null
  // Effective liquidation LTV = collateralValueLiquidation / collateralValueRaw * 100
  // This is the weighted average liquidation LTV across all enabled collaterals
  const effectiveLiquidationLtv = collateralUsd > 0 ? (collateralLiqUsd / collateralUsd) * 100 : null

  return {
    healthFactor,
    ltv,
    effectiveMaxLtv,
    effectiveLiquidationLtv,
    totalCollateralUsd: collateralUsd,
    totalDebtUsd: debtUsd,
  }
}

function getHealthStatus(healthFactor: number | null) {
  if (healthFactor === null)
    return {
      label: 'Healthy',
      color: 'text-teal-400',
      bgColor: 'bg-teal-400/15',
      borderColor: 'border-teal-400/30',
    }
  if (healthFactor >= 3)
    return {
      label: 'Healthy',
      color: 'text-teal-400',
      bgColor: 'bg-teal-400/15',
      borderColor: 'border-teal-400/30',
    }
  if (healthFactor >= 2)
    return {
      label: 'Moderate',
      color: 'text-amber-400',
      bgColor: 'bg-amber-400/15',
      borderColor: 'border-amber-400/30',
    }
  if (healthFactor >= 1)
    return {
      label: 'Risky',
      color: 'text-red-400',
      bgColor: 'bg-red-400/15',
      borderColor: 'border-red-400/30',
    }
  return {
    label: 'Liquidation',
    color: 'text-red-400',
    bgColor: 'bg-red-400/15',
    borderColor: 'border-red-400/30',
  }
}

interface CollateralRowProps {
  collateral: CollateralPosition
  summary: SubAccountSummary
  getLogoForVault: (vaultAddress: Address) => string | undefined
  currentLtv: number
  maxLtvPercent: number
  liqLtvPercent: number
  /** Account-wide collateral USD from Account Lens for accurate dialog calculations */
  accountTotalCollateralUSD: number
  /** EVC address for enable/disable collateral actions */
  evcAddress?: Address
  /** Sub-account ID for the position */
  subAccountId: number
  /** Current debt in USD for health factor calculation */
  currentDebtUSD: number
  indexerVaults?: Map<Address, NormalizedELendInfo>
  cardRouteBadge?: CardRouteBadgeData | null
}

function CollateralRow({
  collateral: c,
  summary,
  getLogoForVault,
  currentLtv,
  maxLtvPercent,
  liqLtvPercent,
  accountTotalCollateralUSD,
  evcAddress,
  subAccountId,
  currentDebtUSD,
  indexerVaults,
  cardRouteBadge,
}: CollateralRowProps) {
  const openDialog = useOpenDialog()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const formattedAmount = Number(formatUnits(c.assets, c.decimals))
  const market = getVaultMarket(c.vaultAddress, indexerVaults?.get(c.vaultAddress)?.products)
  const entityKey = Array.isArray(market?.entity) ? market?.entity[0] : market?.entity
  const entities = getEntities()
  const curator = entityKey ? entities.get(entityKey)?.name : undefined

  // Calculate collateral price from valueUsd / formattedAmount
  const collateralPriceUSD = formattedAmount > 0 ? c.valueUsd / formattedAmount : 0

  const handleViewBorrowPair = useCallback(() => {
    if (!summary.controller) return
    const subAccountParam = subAccountId !== undefined ? `?subAccountId=${subAccountId}` : ''
    router.push(`/markets/eborrow/${c.vaultAddress}/${summary.controller}${subAccountParam}`)
  }, [router, c.vaultAddress, summary.controller, subAccountId])

  const formattedAmountStr = formatUnits(c.assets, c.decimals)

  const handleDisableCollateral = useCallback(() => {
    if (!evcAddress) return
    openDialog(eCollateralStatusDialogConfig as any, {
      collateralVault: c.vaultAddress,
      collateralAsset: c.underlyingAsset,
      collateralSymbol: c.symbol,
      collateralDecimals: c.decimals,
      collateralLogoURI: getLogoForVault(c.vaultAddress),
      currentCollateralFormatted: formattedAmount,
      collateralPriceUSD,
      subAccountId,
      evcAddress,
      action: 'disable',
      currentDebtUSD,
      accountTotalCollateralUSD,
      liquidationLTV: liqLtvPercent,
      currentLTV: currentLtv,
    })
  }, [
    evcAddress,
    c.vaultAddress,
    c.underlyingAsset,
    c.symbol,
    c.decimals,
    getLogoForVault,
    formattedAmount,
    collateralPriceUSD,
    subAccountId,
    openDialog,
    currentDebtUSD,
    accountTotalCollateralUSD,
    liqLtvPercent,
    currentLtv,
  ])

  const handleAssetSwap = useCallback(() => {
    openDialog(positionAssetSwapDialogConfig as any, {
      fromVaultAddress: c.vaultAddress,
      fromVaultSymbol: c.symbol,
      fromAssetSymbol: c.symbol, // Already the underlying asset symbol from metadata.assetSymbol
      fromDecimals: c.decimals,
      fromUnderlyingAsset: c.underlyingAsset,
      userBalance: formattedAmountStr,
      userBalanceUsd: c.valueUsd,
      subAccountId,
      logoURI: getLogoForVault(c.vaultAddress),
    })
  }, [
    c.vaultAddress,
    c.symbol,
    c.decimals,
    c.underlyingAsset,
    c.valueUsd,
    formattedAmountStr,
    subAccountId,
    getLogoForVault,
    openDialog,
  ])

  const handleTransfer = useCallback(() => {
    openDialog(eTransferDialogConfig as any, {
      vaultAddress: c.vaultAddress,
      vaultSymbol: c.symbol,
      vaultDecimals: c.decimals,
      underlyingAsset: c.underlyingAsset,
      assetSymbol: c.symbol,
      assetDecimals: c.decimals,
      logoURI: getLogoForVault(c.vaultAddress),
      priceUSD: collateralPriceUSD,
      currentBalance: formattedAmount,
      sourceSubAccountId: subAccountId,
    })
  }, [
    c.vaultAddress,
    c.symbol,
    c.decimals,
    c.underlyingAsset,
    collateralPriceUSD,
    formattedAmount,
    subAccountId,
    getLogoForVault,
    openDialog,
  ])

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="group/collapsible border-white/[0.06] border-b">
      <Collapsible.Trigger asChild>
        <button type="button" className="group flex w-full cursor-pointer items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <TokenIcon
              token={{ logoURI: getLogoForVault(c.vaultAddress), symbol: TokenSymbol(c.symbol) }}
              className="h-8 w-8"
            />
            <div className="text-left">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-sm text-white">{c.symbol}</span>
                <CardRouteBadge badge={cardRouteBadge ?? null} />
              </div>
              {market && (
                <div className="flex flex-col">
                  {curator && (
                    <Typography variant="span" className="text-muted-foreground text-xs">
                      by {curator}
                    </Typography>
                  )}
                  <Typography variant="span" className="text-muted-foreground text-xs">
                    {market.name} Market
                  </Typography>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end">
              <span className="font-medium text-sm text-white">
                {formattedAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </span>
              <span className="text-muted-foreground text-xs">
                ${c.valueUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <SupplyApyTooltip
                breakdown={{
                  lendingApy: c.lendingApy,
                  intrinsicApy: c.intrinsicApy,
                  totalApy: Number(c.supplyApy) * 100,
                }}
              >
                <span className="text-primary text-xs">{(Number(c.supplyApy) * 100).toFixed(2)}% APY</span>
              </SupplyApyTooltip>
            </div>
            <ChevronDownIcon className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 group-hover:text-foreground" />
          </div>
        </button>
      </Collapsible.Trigger>

      <Collapsible.Content className="pb-3">
        <div className="flex flex-col gap-2">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                openDialog(eAddToPositionDialogConfig as any, {
                  subAccountId,
                  controller: summary.controller,
                  currentDebtUSD,
                  currentLTV: currentLtv,
                  liquidationLTV: liqLtvPercent,
                  initialVaultAddress: c.vaultAddress,
                })
              }}
            >
              Add
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
              onClick={() => {
                const collateralLogoURI = getLogoForVault(c.vaultAddress)
                openDialog(eWithdrawCollateralDialogConfig, {
                  collateralVault: c.vaultAddress,
                  collateralAsset: c.underlyingAsset,
                  collateralSymbol: c.symbol,
                  collateralDecimals: c.decimals,
                  ...(collateralLogoURI && { collateralLogoURI }),
                  collateralPriceUSD: c.valueUsd / formattedAmount || 0,
                  currentCollateralFormatted: formattedAmount,
                  currentDebtUSD: summary.totalDebtUsd,
                  currentLTV: currentLtv,
                  maxLTV: maxLtvPercent,
                  liquidationLTV: liqLtvPercent,
                  ...(accountTotalCollateralUSD > 0 && { accountTotalCollateralUSD }),
                  ...(summary.subAccountId !== null &&
                    summary.subAccountId !== undefined && { subAccountId: summary.subAccountId }),
                })
              }}
              disabled={formattedAmount <= 0}
            >
              Withdraw
            </Button>
            {/* Actions menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/10 hover:text-white"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 p-1">
                {evcAddress && (
                  <DropdownMenuItem onClick={handleDisableCollateral} className="cursor-pointer p-2">
                    <span className="text-sm">Switch to Savings</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleAssetSwap} className="cursor-pointer p-2">
                  <span className="text-sm">Asset Swap</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleTransfer} className="cursor-pointer p-2">
                  <span className="text-sm">Transfer</span>
                </DropdownMenuItem>
                {summary.controller && (
                  <DropdownMenuItem onClick={handleViewBorrowPair} className="cursor-pointer p-2">
                    <span className="text-sm">Go to Borrow</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

/** Savings Row - for deposits that are NOT enabled as collateral */
interface SavingsRowProps {
  collateral: CollateralPosition
  summary: SubAccountSummary
  getLogoForVault: (vaultAddress: Address) => string | undefined
  evcAddress?: Address
  /** Sub-account ID for the position */
  subAccountId: number
  /** Current debt in USD for health factor calculation */
  currentDebtUSD: number
  /** Total collateral currently backing debt (USD) */
  accountTotalCollateralUSD: number
  /** Liquidation LTV threshold */
  liqLtvPercent: number
  /** Current LTV from Account Lens */
  currentLtv: number
  /** Whether this vault is from the same market as the controller (can actually serve as collateral) */
  isSameMarketAsController: boolean
  indexerVaults?: Map<Address, NormalizedELendInfo>
  cardRouteBadge?: CardRouteBadgeData | null
}

function SavingsRow({
  collateral: c,
  summary,
  getLogoForVault,
  evcAddress,
  subAccountId,
  currentDebtUSD,
  accountTotalCollateralUSD,
  liqLtvPercent,
  currentLtv,
  isSameMarketAsController,
  indexerVaults,
  cardRouteBadge,
}: SavingsRowProps) {
  const openDialog = useOpenDialog()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const formattedAmount = Number(formatUnits(c.assets, c.decimals))
  const market = getVaultMarket(c.vaultAddress, indexerVaults?.get(c.vaultAddress)?.products)
  const entityKey = Array.isArray(market?.entity) ? market?.entity[0] : market?.entity
  const entities = getEntities()
  const curator = entityKey ? entities.get(entityKey)?.name : undefined

  // Calculate collateral price from valueUsd / formattedAmount
  const collateralPriceUSD = formattedAmount > 0 ? c.valueUsd / formattedAmount : 0

  const handleViewMarket = useCallback(() => {
    const basePath = paths.eLendDetails
      .replace(':chainId', String(E_LEND_CHAIN_ID))
      .replace(':vaultAddress', c.vaultAddress)
    const subAccountParam = subAccountId !== undefined ? `?subAccountId=${subAccountId}` : ''
    router.push(`${basePath}${subAccountParam}`)
  }, [router, c.vaultAddress, subAccountId])

  const formattedAmountStr = formatUnits(c.assets, c.decimals)

  // Handler: enable collateral (same-market → "Use as Collateral")
  const handleEnableCollateral = useCallback(() => {
    if (!evcAddress) return
    openDialog(eCollateralStatusDialogConfig as any, {
      collateralVault: c.vaultAddress,
      collateralAsset: c.underlyingAsset,
      collateralSymbol: c.symbol,
      collateralDecimals: c.decimals,
      collateralLogoURI: getLogoForVault(c.vaultAddress),
      currentCollateralFormatted: formattedAmount,
      collateralPriceUSD,
      subAccountId,
      evcAddress,
      action: 'enable',
      currentDebtUSD,
      accountTotalCollateralUSD,
      liquidationLTV: liqLtvPercent,
      currentLTV: currentLtv,
      mode: 'collateral',
    })
  }, [
    evcAddress,
    c.vaultAddress,
    c.underlyingAsset,
    c.symbol,
    c.decimals,
    getLogoForVault,
    formattedAmount,
    collateralPriceUSD,
    subAccountId,
    openDialog,
    currentDebtUSD,
    accountTotalCollateralUSD,
    liqLtvPercent,
    currentLtv,
  ])

  const handleAssetSwap = useCallback(() => {
    openDialog(positionAssetSwapDialogConfig as any, {
      fromVaultAddress: c.vaultAddress,
      fromVaultSymbol: c.symbol,
      fromAssetSymbol: c.symbol, // Already the underlying asset symbol from metadata.assetSymbol
      fromDecimals: c.decimals,
      fromUnderlyingAsset: c.underlyingAsset,
      userBalance: formattedAmountStr,
      userBalanceUsd: c.valueUsd,
      subAccountId,
      logoURI: getLogoForVault(c.vaultAddress),
    })
  }, [
    c.vaultAddress,
    c.symbol,
    c.decimals,
    c.underlyingAsset,
    c.valueUsd,
    formattedAmountStr,
    subAccountId,
    getLogoForVault,
    openDialog,
  ])

  const handleTransfer = useCallback(() => {
    openDialog(eTransferDialogConfig as any, {
      vaultAddress: c.vaultAddress,
      vaultSymbol: c.symbol,
      vaultDecimals: c.decimals,
      underlyingAsset: c.underlyingAsset,
      assetSymbol: c.symbol,
      assetDecimals: c.decimals,
      logoURI: getLogoForVault(c.vaultAddress),
      priceUSD: collateralPriceUSD,
      currentBalance: formattedAmount,
      sourceSubAccountId: subAccountId,
    })
  }, [
    c.vaultAddress,
    c.symbol,
    c.decimals,
    c.underlyingAsset,
    collateralPriceUSD,
    formattedAmount,
    subAccountId,
    getLogoForVault,
    openDialog,
  ])

  // Handler: toggle controller (different-market → derives action from current isEnabled state)
  const handleToggleController = useCallback(() => {
    if (!evcAddress) return
    openDialog(eCollateralStatusDialogConfig as any, {
      collateralVault: c.vaultAddress,
      collateralAsset: c.underlyingAsset,
      collateralSymbol: c.symbol,
      collateralDecimals: c.decimals,
      collateralLogoURI: getLogoForVault(c.vaultAddress),
      currentCollateralFormatted: formattedAmount,
      collateralPriceUSD,
      subAccountId,
      evcAddress,
      action: c.isEnabled ? 'disable' : 'enable',
      currentDebtUSD,
      accountTotalCollateralUSD,
      liquidationLTV: liqLtvPercent,
      currentLTV: currentLtv,
      mode: 'controller',
    })
  }, [
    evcAddress,
    c.vaultAddress,
    c.underlyingAsset,
    c.symbol,
    c.decimals,
    c.isEnabled,
    getLogoForVault,
    formattedAmount,
    collateralPriceUSD,
    subAccountId,
    openDialog,
    currentDebtUSD,
    accountTotalCollateralUSD,
    liqLtvPercent,
    currentLtv,
  ])

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="group/collapsible border-white/[0.06] border-b">
      <Collapsible.Trigger asChild>
        <button type="button" className="group flex w-full cursor-pointer items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <TokenIcon
              token={{ logoURI: getLogoForVault(c.vaultAddress), symbol: TokenSymbol(c.symbol) }}
              className="h-8 w-8"
            />
            <div className="text-left">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-sm text-white">{c.symbol}</span>
                <CardRouteBadge badge={cardRouteBadge ?? null} />
              </div>
              {market && (
                <div className="flex flex-col">
                  {curator && (
                    <Typography variant="span" className="text-muted-foreground text-xs">
                      by {curator}
                    </Typography>
                  )}
                  <Typography variant="span" className="text-muted-foreground text-xs">
                    {market.name} Market
                  </Typography>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end">
              <span className="font-medium text-sm text-white">
                {formattedAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </span>
              <span className="text-muted-foreground text-xs">
                ${c.valueUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <SupplyApyTooltip
                breakdown={{
                  lendingApy: c.lendingApy,
                  intrinsicApy: c.intrinsicApy,
                  totalApy: Number(c.supplyApy) * 100,
                }}
              >
                <span className="text-primary text-xs">{(Number(c.supplyApy) * 100).toFixed(2)}% APY</span>
              </SupplyApyTooltip>
            </div>
            <ChevronDownIcon className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 group-hover:text-foreground" />
          </div>
        </button>
      </Collapsible.Trigger>

      <Collapsible.Content className="pb-3">
        <div className="flex flex-col gap-2">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                openDialog(eAddToPositionDialogConfig as any, {
                  subAccountId,
                  controller: summary.controller,
                  currentDebtUSD,
                  currentLTV: currentLtv,
                  liquidationLTV: liqLtvPercent,
                  initialVaultAddress: c.vaultAddress,
                })
              }}
            >
              Add
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
              onClick={() => {
                const collateralLogoURI = getLogoForVault(c.vaultAddress)
                openDialog(eWithdrawCollateralDialogConfig, {
                  collateralVault: c.vaultAddress,
                  collateralAsset: c.underlyingAsset,
                  collateralSymbol: c.symbol,
                  collateralDecimals: c.decimals,
                  ...(collateralLogoURI && { collateralLogoURI }),
                  collateralPriceUSD: collateralPriceUSD,
                  currentCollateralFormatted: formattedAmount,
                  currentDebtUSD: 0,
                  currentLTV: 0,
                  maxLTV: 0,
                  liquidationLTV: 0,
                  ...(summary.subAccountId !== null &&
                    summary.subAccountId !== undefined && { subAccountId: summary.subAccountId }),
                })
              }}
              disabled={formattedAmount <= 0}
            >
              Withdraw
            </Button>
            {/* Actions menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/10 hover:text-white"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 p-1">
                {evcAddress && isSameMarketAsController && (
                  <DropdownMenuItem onClick={handleEnableCollateral} className="cursor-pointer p-2">
                    <span className="text-sm">Use as Collateral</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleAssetSwap} className="cursor-pointer p-2">
                  <span className="text-sm">Asset Swap</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleTransfer} className="cursor-pointer p-2">
                  <span className="text-sm">Transfer</span>
                </DropdownMenuItem>
                {evcAddress && !isSameMarketAsController && (
                  <DropdownMenuItem onClick={handleToggleController} className="cursor-pointer p-2">
                    <span className="text-sm">{c.isEnabled ? 'Disable Controller' : 'Enable Controller'}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleViewMarket} className="cursor-pointer p-2">
                  <span className="text-sm">Go to Lend</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

function PositionLabel({ subAccountId, className }: { subAccountId: number; className?: string }) {
  const { name } = usePositionName(subAccountId)
  return <span className={className}>{name}</span>
}

interface PositionCardProps {
  summary: SubAccountSummary
  getLogoForVault: (vaultAddress: Address) => string | undefined
  ltvConfigs: LtvConfig[]
  evcAddress: Address | undefined
  indexerVaults?: Map<Address, NormalizedELendInfo>
  cardSolverConfigs: PersistedCardSolverConfig[]
}

function getFormattedHealthFactor(healthFactor: number | null) {
  if (healthFactor === null) return '∞'
  if (healthFactor >= 4) return '4+'
  if (healthFactor === 0) return 0
  return healthFactor.toFixed(2)
}

function getEulerPositionCardRouteBadge(
  cardSolverConfigs: PersistedCardSolverConfig[],
  summary: SubAccountSummary,
): CardRouteBadgeData | null {
  const cardsById = new Map<string, PersistedCardSolverConfig>()

  for (const borrow of summary.borrows) {
    const badge = getEulerBorrowCardRouteBadge(cardSolverConfigs, {
      borrowVaultAddress: borrow.vaultAddress,
      subAccountId: summary.subAccountId,
    })
    for (const card of badge?.cards ?? []) {
      cardsById.set(card.cardID, card)
    }
  }

  for (const collateral of summary.collaterals) {
    const badge = getEulerSpendCardRouteBadge(cardSolverConfigs, {
      supplyVaultAddress: collateral.vaultAddress,
      subAccountId: summary.subAccountId,
    })
    for (const card of badge?.cards ?? []) {
      cardsById.set(card.cardID, card)
    }
  }

  const cards = Array.from(cardsById.values())
  return cards.length > 0 ? { cards, count: cards.length } : null
}

function PositionCard({
  summary,
  getLogoForVault,
  ltvConfigs,
  evcAddress,
  indexerVaults,
  cardSolverConfigs,
}: PositionCardProps) {
  const openDialog = useOpenDialog()
  const healthStatus = getHealthStatus(summary.healthFactor)
  const {
    name: positionName,
    setName: setPositionName,
    clearName: clearPositionName,
    isCustom: hasCustomName,
  } = usePositionName(summary.subAccountId ?? 0)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editValue, setEditValue] = useState('')
  const nameInputRef = useRef<HTMLInputElement>(null)
  const positionCardRouteBadge = useMemo(
    () => getEulerPositionCardRouteBadge(cardSolverConfigs, summary),
    [cardSolverConfigs, summary],
  )

  // Get controller's market key for market compatibility checks
  const controllerMarketKey = summary.controller
    ? getVaultMarket(summary.controller, indexerVaults?.get(summary.controller)?.products)?.key
    : undefined

  // Split collaterals: only show in "Collateral" section if enabled AND same market as controller
  const enabledCollaterals = summary.collaterals.filter((c) => {
    if (!c.isEnabled) return false
    if (!controllerMarketKey) return true
    const collateralMarketKey = getVaultMarket(c.vaultAddress, indexerVaults?.get(c.vaultAddress)?.products)?.key
    return collateralMarketKey === controllerMarketKey
  })

  // Savings: disabled vaults + enabled vaults from different markets
  const savingsCollaterals = summary.collaterals.filter((c) => {
    if (!c.isEnabled) return true
    if (!controllerMarketKey) return false
    const collateralMarketKey = getVaultMarket(c.vaultAddress, indexerVaults?.get(c.vaultAddress)?.products)?.key
    return collateralMarketKey !== controllerMarketKey
  })

  // Get max LTV and liquidation LTV from config (use first matching config)
  const primaryCollateral = enabledCollaterals[0]
  const primaryBorrow = summary.borrows[0]

  // Find any LTV config with non-zero borrowLtv for collaterals in this position
  // (the primary collateral might not have a valid LTV config for this liability)
  const ltvConfig =
    ltvConfigs.find(
      (c) =>
        primaryBorrow &&
        c.liabilityVault.toLowerCase() === primaryBorrow.vaultAddress.toLowerCase() &&
        c.borrowLtv > 0 &&
        enabledCollaterals.some(
          (collateral) => collateral.vaultAddress.toLowerCase() === c.collateralVault.toLowerCase(),
        ),
    ) ??
    ltvConfigs.find(
      // Fallback to primary collateral config even if borrowLtv is 0 (for display purposes)
      (c) =>
        primaryCollateral &&
        primaryBorrow &&
        c.collateralVault.toLowerCase() === primaryCollateral.vaultAddress.toLowerCase() &&
        c.liabilityVault.toLowerCase() === primaryBorrow.vaultAddress.toLowerCase(),
    )

  // Use non-zero values or sensible defaults
  // Prefer Account Lens effective LTVs for multi-collateral positions (weighted average)
  const configMaxLtvPercent = ltvConfig && ltvConfig.borrowLtv > 0 ? ltvConfig.borrowLtv * 100 : 80
  const configLiqLtvPercent = ltvConfig && ltvConfig.liquidationLtv > 0 ? ltvConfig.liquidationLtv * 100 : 90
  const maxLtvPercent = summary.effectiveMaxLtv ?? configMaxLtvPercent
  const liqLtvPercent = summary.effectiveLiquidationLtv ?? configLiqLtvPercent
  const currentLtv = summary.ltv ?? 0

  // Calculate LTV bar width (as percentage of liquidation LTV)
  const ltvBarWidth = liqLtvPercent > 0 ? Math.min((currentLtv / liqLtvPercent) * 100, 100) : 0
  const maxLtvBarPosition = liqLtvPercent > 0 ? (maxLtvPercent / liqLtvPercent) * 100 : 0

  // Handle opening the Add to Position dialog
  const handleAddToPosition = useCallback(() => {
    if (!evcAddress) return
    openDialog(eAddToPositionDialogConfig as any, {
      subAccountId: summary.subAccountId ?? 0,
      controller: summary.controller, // Pass controller (liability vault) to filter compatible collaterals
      currentDebtUSD: summary.totalDebtUsd,
      currentLTV: currentLtv,
      liquidationLTV: liqLtvPercent,
    })
  }, [
    openDialog,
    summary.subAccountId,
    summary.controller,
    summary.totalDebtUsd,
    evcAddress,
    currentLtv,
    liqLtvPercent,
  ])
  const formattedHealthFactor = getFormattedHealthFactor(summary.healthFactor)

  return (
    <div className="no-scrollbar flex h-full flex-col items-start gap-5 overflow-y-auto rounded-2xl border border-border bg-panel p-6 shadow-2xs lg:max-h-[560px]">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-1">
          {isEditingName ? (
            <div className="flex items-center gap-1">
              <input
                ref={nameInputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const trimmed = editValue.trim()
                    if (trimmed) {
                      setPositionName(trimmed)
                    }
                    setIsEditingName(false)
                  } else if (e.key === 'Escape') {
                    setIsEditingName(false)
                  }
                }}
                maxLength={18}
                className="h-7 w-40 rounded border border-white/20 bg-white/5 px-2 text-foreground text-lg outline-none focus:border-primary"
                autoFocus
              />
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const trimmed = editValue.trim()
                  if (trimmed) {
                    setPositionName(trimmed)
                  }
                  setIsEditingName(false)
                }}
                className="flex h-6 w-6 items-center justify-center rounded text-emerald-400 transition-colors hover:bg-white/10"
                title="Save"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setIsEditingName(false)}
                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-white/10"
                title="Cancel"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <>
              <Typography variant="h3" className="text-lg">
                {positionName}
              </Typography>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44 p-1">
                  {evcAddress && (
                    <DropdownMenuItem onClick={handleAddToPosition} className="cursor-pointer p-2">
                      <Plus className="mr-2 h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">Add to Position</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => {
                      setEditValue(hasCustomName ? positionName : '')
                      setIsEditingName(true)
                    }}
                    className="cursor-pointer p-2"
                  >
                    <Pencil className="mr-2 h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">Rename</span>
                  </DropdownMenuItem>
                  {hasCustomName && (
                    <DropdownMenuItem onClick={() => clearPositionName()} className="cursor-pointer p-2">
                      <X className="mr-2 h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">Reset Name</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CardRouteBadge badge={positionCardRouteBadge} />
          <div className={cn('rounded-[8px] border px-4 py-1', healthStatus.bgColor, healthStatus.borderColor)}>
            <span className={cn('text-xs', healthStatus.color)}>{formattedHealthFactor}</span>
          </div>
        </div>
      </div>

      {/* Collateral Section - enabled collaterals that back borrowing */}
      <div className="flex w-full flex-1 flex-col">
        <div className="mb-2 flex items-center gap-1.5">
          <span className="font-medium text-muted-foreground text-xs">Collateral</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-3 w-3 cursor-help text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContentShort>
              Collateral backs your borrowing position. These deposits contribute to your LTV and borrowing capacity.
            </TooltipContentShort>
          </Tooltip>
        </div>
        {enabledCollaterals.length === 0 ? (
          <div className="py-3 text-muted-foreground text-sm">No collateral</div>
        ) : (
          enabledCollaterals.map((c) => (
            <CollateralRow
              key={c.vaultAddress}
              collateral={c}
              summary={summary}
              getLogoForVault={getLogoForVault}
              currentLtv={currentLtv}
              maxLtvPercent={maxLtvPercent}
              liqLtvPercent={liqLtvPercent}
              accountTotalCollateralUSD={summary.oracleCollateralUsd ?? summary.totalCollateralUsd}
              evcAddress={evcAddress}
              subAccountId={summary.subAccountId ?? 0}
              currentDebtUSD={summary.totalDebtUsd}
              indexerVaults={indexerVaults}
              cardRouteBadge={getEulerSpendCardRouteBadge(cardSolverConfigs, {
                supplyVaultAddress: c.vaultAddress,
                subAccountId: summary.subAccountId,
              })}
            />
          ))
        )}
      </div>

      {/* Savings Section - disabled collaterals that don't back borrowing */}
      {savingsCollaterals.length > 0 && (
        <div className="flex w-full flex-col">
          <div className="mb-2 flex items-center gap-1.5">
            <span className="font-medium text-amber-400/80 text-xs">Savings</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3 w-3 cursor-help text-amber-400/60" />
              </TooltipTrigger>
              <TooltipContentShort>
                Savings are supply-only deposits that earn yield but are NOT used as collateral. They don&apos;t
                contribute to your LTV or borrowing capacity.
              </TooltipContentShort>
            </Tooltip>
          </div>
          {savingsCollaterals.map((c) => {
            const collateralMarketKey = getVaultMarket(
              c.vaultAddress,
              indexerVaults?.get(c.vaultAddress)?.products,
            )?.key
            const isSameMarket = !controllerMarketKey || collateralMarketKey === controllerMarketKey
            return (
              <SavingsRow
                key={c.vaultAddress}
                collateral={c}
                summary={summary}
                getLogoForVault={getLogoForVault}
                evcAddress={evcAddress}
                subAccountId={summary.subAccountId ?? 0}
                currentDebtUSD={summary.totalDebtUsd}
                accountTotalCollateralUSD={summary.oracleCollateralUsd ?? summary.totalCollateralUsd}
                liqLtvPercent={liqLtvPercent}
                currentLtv={currentLtv}
                isSameMarketAsController={isSameMarket}
                indexerVaults={indexerVaults}
                cardRouteBadge={getEulerSpendCardRouteBadge(cardSolverConfigs, {
                  supplyVaultAddress: c.vaultAddress,
                  subAccountId: summary.subAccountId,
                })}
              />
            )
          })}
        </div>
      )}

      {/* Debt Section - pushed to bottom */}
      <div className="mt-auto flex w-full flex-col">
        <span className="mb-2 font-medium text-muted-foreground text-xs">Debt</span>
        {summary.borrows.length === 0 ? (
          <div className="rounded-lg border bg-panel px-4 py-3 text-muted-foreground text-sm">No debt</div>
        ) : (
          summary.borrows.map((b) => {
            const formattedDebt = Number(formatUnits(b.debt, b.decimals))
            const liabilityPriceUSD = formattedDebt > 0 ? b.debtUsd / formattedDebt : 0
            const debtMarket = getVaultMarket(b.vaultAddress, indexerVaults?.get(b.vaultAddress)?.products)
            const debtEntityKey = Array.isArray(debtMarket?.entity) ? debtMarket?.entity[0] : debtMarket?.entity
            const debtEntities = getEntities()
            const debtCurator = debtEntityKey ? debtEntities.get(debtEntityKey)?.name : undefined

            // For max LTV, prefer Account Lens effectiveMaxLtv (weighted average across all collaterals)
            // This is the correct value for multi-collateral positions
            // Only fall back to config lookup if Account Lens data isn't available
            const borrowLtvConfig = ltvConfigs.find(
              (config) =>
                config.liabilityVault.toLowerCase() === b.vaultAddress.toLowerCase() &&
                config.borrowLtv > 0 &&
                summary.collaterals.some((c) => c.vaultAddress.toLowerCase() === config.collateralVault.toLowerCase()),
            )
            const configBorrowMaxLtv = borrowLtvConfig ? borrowLtvConfig.borrowLtv * 100 : 80
            const borrowMaxLtvPercent = summary.effectiveMaxLtv ?? configBorrowMaxLtv

            return (
              <div key={b.vaultAddress} className="flex flex-col gap-3 border-white/[0.06] border-b py-3">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    <TokenIcon
                      token={{ logoURI: getLogoForVault(b.vaultAddress), symbol: TokenSymbol(b.symbol) }}
                      className="h-8 w-8"
                    />
                    <div className="text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-sm text-white">{b.symbol}</span>
                        <CardRouteBadge
                          badge={getEulerBorrowCardRouteBadge(cardSolverConfigs, {
                            borrowVaultAddress: b.vaultAddress,
                            subAccountId: summary.subAccountId,
                          })}
                        />
                      </div>
                      {debtMarket && (
                        <div className="flex flex-col">
                          {debtCurator && (
                            <Typography variant="span" className="text-muted-foreground text-xs">
                              by {debtCurator}
                            </Typography>
                          )}
                          <Typography variant="span" className="text-muted-foreground text-xs">
                            {debtMarket.name} Market
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <span className="font-medium text-sm text-white">
                      {formattedDebt.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      ${b.debtUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-gradient-yellow text-xs">{(Number(b.borrowApy) * 100).toFixed(2)}% APY</span>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      if (!evcAddress) return
                      const liabilityLogoURI = getLogoForVault(b.vaultAddress)
                      openDialog(eBorrowMoreDialogConfig, {
                        liabilityVault: b.vaultAddress,
                        liabilitySymbol: b.symbol,
                        liabilityDecimals: b.decimals,
                        liabilityAsset: b.underlyingAsset,
                        ...(liabilityLogoURI && { liabilityLogoURI }),
                        currentDebtFormatted: formattedDebt,
                        currentLTV: currentLtv,
                        maxLTV: borrowMaxLtvPercent,
                        collateralValueUSD: summary.oracleCollateralUsd ?? summary.totalCollateralUsd,
                        liabilityPriceUSD,
                        ...(summary.subAccountId !== null &&
                          summary.subAccountId !== undefined && { subAccountId: summary.subAccountId }),
                      })
                    }}
                    disabled={summary.totalCollateralUsd <= 0 || !evcAddress}
                  >
                    Borrow
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      if (summary.collaterals.length === 0) return
                      const liabilityLogoURI = getLogoForVault(b.vaultAddress)

                      const collateralOptions: CollateralOption[] = summary.collaterals.map((c) => ({
                        vaultAddress: c.vaultAddress,
                        symbol: c.symbol,
                        decimals: c.decimals,
                        assetAddress: c.underlyingAsset,
                        logoURI: getLogoForVault(c.vaultAddress),
                        balance: formatUnits(c.assets, c.decimals),
                        shares: c.shares?.toString(),
                      }))

                      openDialog<Omit<EBorrowUnifiedRepayDialogProps, 'open' | 'setOpen'>>(
                        eBorrowUnifiedRepayDialogConfig,
                        {
                          liabilityVault: b.vaultAddress,
                          liabilitySymbol: b.symbol,
                          liabilityDecimals: b.decimals,
                          liabilityAsset: b.underlyingAsset,
                          maxRepayAmount: formattedDebt.toString(),
                          ...(liabilityLogoURI && { liabilityLogoURI }),
                          collateralOptions,
                          ...(summary.subAccountId !== null &&
                            summary.subAccountId !== undefined && { subAccountId: summary.subAccountId }),
                        },
                      )
                    }}
                    disabled={formattedDebt <= 0}
                  >
                    Repay
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/10 hover:text-white"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44 p-1">
                      <DropdownMenuItem
                        onClick={() => {
                          const liabilityLogoURI = getLogoForVault(b.vaultAddress)
                          openDialog(evcSwapDebtDialogConfig as any, {
                            fromVault: b.vaultAddress,
                            fromSymbol: b.symbol,
                            fromDecimals: b.decimals,
                            fromAsset: b.underlyingAsset,
                            ...(liabilityLogoURI && { fromLogoURI: liabilityLogoURI }),
                            currentDebt: formattedDebt.toString(),
                            ...(summary.subAccountId !== null &&
                              summary.subAccountId !== undefined && { subAccountId: summary.subAccountId }),
                            collateralVaults: enabledCollaterals.map((c) => c.vaultAddress),
                          })
                        }}
                        disabled={formattedDebt <= 0}
                        className="cursor-pointer p-2"
                      >
                        <span className="text-sm">Swap Debt</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* LTV Bar */}
      {summary.borrows.length > 0 && (
        <div className="flex w-full flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">Loan-to-Value (Account)</span>
            <span
              className={cn(
                'font-medium text-sm',
                currentLtv >= liqLtvPercent
                  ? 'text-red-400'
                  : currentLtv >= maxLtvPercent
                    ? 'text-orange-400'
                    : 'text-white',
              )}
            >
              {currentLtv.toFixed(2)}%
            </span>
          </div>

          {/* LTV Progress Bar */}
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/10">
            {/* Current LTV fill */}
            <div
              className={cn(
                'absolute h-full rounded-full transition-all',
                currentLtv >= liqLtvPercent
                  ? 'bg-red-500'
                  : currentLtv >= maxLtvPercent
                    ? 'bg-orange-500'
                    : 'bg-gradient-to-r from-gradient-yellow to-gradient-green',
              )}
              style={{ width: `${ltvBarWidth}%` }}
            />
            {/* Max LTV marker */}
            {maxLtvBarPosition > 0 && maxLtvBarPosition < 100 && (
              <div className="absolute top-0 bottom-0 w-0.5 bg-yellow-500" style={{ left: `${maxLtvBarPosition}%` }} />
            )}
          </div>

          {/* LTV Legend */}
          <div className="flex items-center justify-between text-white/30 text-xs">
            <span>0%</span>
            <span>
              Max: {maxLtvPercent.toFixed(0)}% · Liq: {liqLtvPercent.toFixed(0)}%
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export function SubAccountPositions({
  vaultAddresses,
  ltvConfigs = [],
  vaultLogos,
  compact = false,
  className,
  hideIfEmpty = true,
  filterCollateralVault,
  filterLiabilityVault,
}: SubAccountPositionsProps) {
  const { address: userAddress } = useAccount()
  const { byAddress: cardSolverConfigsByAddress } = useCardSolverConfigs()
  const isStoreHydrated = useStoreHydrated()
  const { data: indexerVaults } = useEulerIndexerELends(E_LEND_CHAIN_ID)
  const cardSolverConfigs = useMemo(
    () =>
      isStoreHydrated && userAddress ? Object.values(cardSolverConfigsByAddress[userAddress.toLowerCase()] ?? {}) : [],
    [cardSolverConfigsByAddress, isStoreHydrated, userAddress],
  )

  const { state, isLoading, evcAddress } = useUserCollateralState({
    vaultAddresses,
    userAddress,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!userAddress && vaultAddresses.length > 0,
  })
  useCleanupClosedPositionNames({ owner: userAddress, state, isLoading })

  // Get unique collateral-liability pairs to fetch LTV configs for
  const ltvPairs = useMemo(() => {
    if (!state || ltvConfigs.length > 0) return [] // Skip if configs already provided
    const pairs: { collateral: Address; liability: Address }[] = []
    const seen = new Set<string>()

    for (const borrow of state.borrows) {
      // Find collaterals on the same subaccount
      const subAccountCollaterals = state.collaterals.filter((c) => c.subAccountId === borrow.subAccountId)
      for (const collateral of subAccountCollaterals) {
        const key = `${collateral.vaultAddress.toLowerCase()}-${borrow.vaultAddress.toLowerCase()}`
        if (!seen.has(key)) {
          seen.add(key)
          pairs.push({ collateral: collateral.vaultAddress, liability: borrow.vaultAddress })
        }
      }
    }
    return pairs
  }, [state, ltvConfigs.length])

  // Fetch LTV configs for each collateral-liability pair
  const ltvConfigContracts = useMemo(
    () =>
      ltvPairs.map(({ collateral, liability }) => ({
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'LTVFull' as const,
        args: [collateral] as const,
        chainId: E_LEND_CHAIN_ID,
      })) satisfies UseReadContractsParameters['contracts'],
    [ltvPairs],
  )

  const { data: ltvConfigData, isLoading: isLoadingLtvConfigs } = useReadContracts({
    contracts: ltvConfigContracts,
    query: {
      enabled: ltvConfigContracts.length > 0,
      staleTime: 60_000, // LTV configs don't change often
    },
  })

  // Parse LTV config data into LtvConfig array
  const fetchedLtvConfigs = useMemo<LtvConfig[]>(() => {
    if (!ltvConfigData || ltvPairs.length === 0) return []

    return ltvPairs
      .map((pair, idx) => {
        // LTVFull returns: borrowLTV (uint16), liquidationLTV (uint16), initialLiquidationLTV, targetTimestamp, rampDuration
        const result = ltvConfigData[idx]?.result as readonly [number, number, number, bigint, number] | undefined
        if (!result) return null

        const [borrowLTV, liquidationLTV] = result
        return {
          collateralVault: pair.collateral,
          liabilityVault: pair.liability,
          borrowLtv: borrowLTV / 10000, // Convert from basis points to decimal
          liquidationLtv: liquidationLTV / 10000,
        }
      })
      .filter((c): c is LtvConfig => c !== null)
  }, [ltvConfigData, ltvPairs])

  // Use provided ltvConfigs or fetched ones
  const effectiveLtvConfigs = ltvConfigs.length > 0 ? ltvConfigs : fetchedLtvConfigs

  // Identify sub-accounts with debt that need Account Lens queries
  const subAccountsWithDebt = useMemo(() => {
    if (!state || !userAddress) return []

    const accounts: { subAccountId: number; subAccountAddress: Address; controller: Address }[] = []
    const seen = new Set<number>()

    for (const borrow of state.borrows) {
      if (seen.has(borrow.subAccountId)) continue
      seen.add(borrow.subAccountId)

      const controller = state.controllers.get(borrow.subAccountId)
      if (!controller) continue

      const subAccountAddress = getSubAccountAddress(userAddress, borrow.subAccountId)
      accounts.push({
        subAccountId: borrow.subAccountId,
        subAccountAddress,
        controller,
      })
    }

    return accounts
  }, [state, userAddress])

  // Fetch Account Lens data for all sub-accounts with debt
  const accountLensContracts = useMemo(
    () =>
      subAccountsWithDebt.map(({ subAccountAddress, controller }) => ({
        address: ACCOUNT_LENS_ADDRESS,
        abi: accountLensAbi,
        functionName: 'getAccountInfo' as const,
        args: [subAccountAddress, controller] as const,
        chainId: E_LEND_CHAIN_ID,
      })) satisfies UseReadContractsParameters['contracts'],
    [subAccountsWithDebt],
  )

  const { data: accountLensData, isLoading: isLoadingAccountLens } = useReadContracts({
    contracts: accountLensContracts,
    query: {
      enabled: accountLensContracts.length > 0,
      staleTime: 30_000, // 30 seconds
      refetchInterval: 30_000,
    },
  })

  // Parse Account Lens results into a map by subAccountId
  const accountLensResults = useMemo(() => {
    const results = new Map<
      number,
      {
        healthFactor: number | null
        ltv: number | null
        effectiveMaxLtv: number | null
        effectiveLiquidationLtv: number | null
        totalCollateralUsd: number
        totalDebtUsd: number
      }
    >()

    if (!accountLensData) return results

    for (let i = 0; i < subAccountsWithDebt.length; i++) {
      const { subAccountId } = subAccountsWithDebt[i]!
      const result = accountLensData[i]

      if (result?.status === 'success' && result.result) {
        const liquidityInfo = (
          result.result as {
            vaultAccountInfo: {
              liquidityInfo: {
                queryFailure: boolean
                liabilityValueLiquidation: bigint
                collateralValueLiquidation: bigint
                collateralValueBorrowing: bigint
                collateralValueRaw: bigint
              }
            }
          }
        ).vaultAccountInfo.liquidityInfo

        const parsed = parseAccountLensData(liquidityInfo)
        // Only add to map if parsing succeeded (null means query failure)
        if (parsed) {
          results.set(subAccountId, parsed)
        }
      }
    }

    return results
  }, [accountLensData, subAccountsWithDebt])

  // Group positions by subaccount and apply route filter
  const subAccountSummaries = useMemo<SubAccountSummary[]>(() => {
    if (!state || !userAddress) return []

    const summaries: SubAccountSummary[] = []
    const activeSubAccounts = new Set<number>()

    for (const collateral of state.collaterals) {
      activeSubAccounts.add(collateral.subAccountId)
    }
    for (const borrow of state.borrows) {
      activeSubAccounts.add(borrow.subAccountId)
    }

    for (const subAccountId of Array.from(activeSubAccounts).sort((a, b) => a - b)) {
      const collaterals = state.collaterals.filter((c) => c.subAccountId === subAccountId)
      const borrows = state.borrows.filter((b) => b.subAccountId === subAccountId)
      const controller = state.controllers.get(subAccountId) || null
      const subAccountAddress = getSubAccountAddress(userAddress, subAccountId)

      // Apply route filter if specified
      if (filterCollateralVault) {
        const hasFilterCollateral = collaterals.some(
          (c) => c.vaultAddress.toLowerCase() === filterCollateralVault.toLowerCase(),
        )
        if (!hasFilterCollateral) continue

        if (filterLiabilityVault) {
          const hasBorrow = borrows.length > 0
          if (hasBorrow) {
            const borrowMatchesFilter = borrows.some(
              (b) => b.vaultAddress.toLowerCase() === filterLiabilityVault.toLowerCase(),
            )
            if (!borrowMatchesFilter) continue
          }
        }
      }

      // Calculate local totals from individual positions (these match what's displayed in the UI)
      const localCollateralUsd = collaterals.reduce((sum, c) => sum + c.valueUsd, 0)
      const localDebtUsd = borrows.reduce((sum, b) => sum + b.debtUsd, 0)

      // Use Account Lens data for health factor and LTV (authoritative on-chain values)
      // but use local totals for display consistency (matches individual item values shown)
      const accountLensResult = accountLensResults.get(subAccountId)

      // Use local totals for display (ensures header totals match sum of displayed items)
      const totalCollateralUsd = localCollateralUsd
      const totalDebtUsd = localDebtUsd
      const healthFactor = accountLensResult?.healthFactor ?? null
      const ltv =
        accountLensResult?.ltv ??
        (localCollateralUsd > 0 && localDebtUsd > 0 ? (localDebtUsd / localCollateralUsd) * 100 : null)
      // Effective max LTV = weighted average of all collateral max LTVs from Account Lens
      const effectiveMaxLtv = accountLensResult?.effectiveMaxLtv ?? null
      // Effective liquidation LTV = weighted average of all collateral liquidation LTVs from Account Lens
      const effectiveLiquidationLtv = accountLensResult?.effectiveLiquidationLtv ?? null
      const oracleCollateralUsd = accountLensResult?.totalCollateralUsd ?? null

      summaries.push({
        subAccountId,
        collaterals,
        borrows,
        controller,
        totalCollateralUsd,
        totalDebtUsd,
        healthFactor,
        ltv,
        effectiveMaxLtv,
        effectiveLiquidationLtv,
        subAccountAddress,
        oracleCollateralUsd,
      })
    }

    return summaries
  }, [state, userAddress, filterCollateralVault, filterLiabilityVault, accountLensResults])

  const getLogoForVault = (vaultAddress: Address): string | undefined => {
    return vaultLogos?.get(vaultAddress.toLowerCase())
  }

  // Only show skeleton on initial load when we have no data yet
  // Once we have positions, don't show skeleton during refetches
  const hasData = subAccountSummaries.length > 0
  const isInitialLoading = (isLoading || isLoadingLtvConfigs || isLoadingAccountLens) && !hasData

  if (!userAddress) return null
  if (hideIfEmpty && !isInitialLoading && subAccountSummaries.length === 0) return null

  if (compact) {
    return (
      <div className={cn('space-y-2', className)}>
        {isInitialLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          subAccountSummaries.map((summary) => (
            <div
              key={summary.subAccountId}
              className="flex items-center gap-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-2.5"
            >
              <PositionLabel subAccountId={summary.subAccountId} className="font-medium text-emerald-400 text-xs" />
              <div className="h-4 w-px bg-emerald-500/20" />

              {/* Collaterals */}
              <div className="flex items-center gap-3">
                {summary.collaterals.map((c) => (
                  <div key={c.vaultAddress} className="flex items-center gap-1.5">
                    <TokenIcon
                      token={{ logoURI: getLogoForVault(c.vaultAddress), symbol: TokenSymbol(c.symbol) }}
                      className="h-4 w-4"
                    />
                    <span className="text-foreground text-sm">
                      {Number(formatUnits(c.assets, c.decimals)).toFixed(4)} {c.symbol}
                    </span>
                    <span className="text-muted-foreground text-xs">${c.valueUsd.toFixed(2)}</span>
                    <SupplyApyTooltip
                      breakdown={{
                        lendingApy: c.lendingApy,
                        intrinsicApy: c.intrinsicApy,
                        totalApy: Number(c.supplyApy) * 100,
                      }}
                    >
                      <span className="text-primary text-xs">{(Number(c.supplyApy) * 100).toFixed(2)}% APY</span>
                    </SupplyApyTooltip>
                  </div>
                ))}
              </div>

              {/* Borrows */}
              {summary.borrows.length > 0 && (
                <>
                  <div className="h-4 w-px bg-white/10" />
                  <div className="flex items-center gap-3">
                    {summary.borrows.map((b) => (
                      <div key={b.vaultAddress} className="flex items-center gap-1.5">
                        <TokenIcon
                          token={{ logoURI: getLogoForVault(b.vaultAddress), symbol: TokenSymbol(b.symbol) }}
                          className="h-4 w-4"
                        />
                        <span className="text-red-400 text-sm">
                          -{Number(formatUnits(b.debt, b.decimals)).toFixed(4)} {b.symbol}
                        </span>
                        <span className="text-muted-foreground text-xs">${b.debtUsd.toFixed(2)}</span>
                        <span className="text-gradient-yellow text-xs">
                          {(Number(b.borrowApy) * 100).toFixed(2)}% APY
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Health Factor */}
              {summary.healthFactor !== null && (
                <>
                  <div className="h-4 w-px bg-white/10" />
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground text-xs">HF:</span>
                    <span
                      className={cn(
                        summary.healthFactor >= 2
                          ? 'text-green-400'
                          : summary.healthFactor >= 1.5
                            ? 'text-yellow-400'
                            : summary.healthFactor >= 1.1
                              ? 'text-orange-400'
                              : 'text-red-400',
                      )}
                    >
                      {summary.healthFactor.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    )
  }

  // Card-based view (default)
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {isInitialLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3 sm:grid-cols-2">
          {subAccountSummaries.map((summary) => (
            <PositionCard
              key={summary.subAccountId}
              summary={summary}
              getLogoForVault={getLogoForVault}
              ltvConfigs={effectiveLtvConfigs}
              evcAddress={evcAddress}
              indexerVaults={indexerVaults}
              cardSolverConfigs={cardSolverConfigs}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export { useUserCollateralState }
